import type { JobApplication } from './applicationTrackingService';
import { formatLpaRange, highestSalaryLabelFromJobs } from './salaryIntelligenceService';
import type { UserCalendarReminder } from '../types/objectivesTasks';
import { countFollowUpsDue } from '../modules/dashboard/utils/jobHuntMetrics';

export interface DailyBriefViewModel {
  newMatchCount: number;
  strongMatchCount: number;
  highestSalaryLabel: string | null;
  followUpsDue: number;
  interviewsTomorrow: number;
  interviewTitle: string | null;
  hasContent: boolean;
}

export interface DailyBriefJobInput {
  score: number;
  salaryMin?: number | null;
  salaryMax?: number | null;
}

function tomorrowDateStr(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function countInterviewsTomorrow(
  applications: JobApplication[],
  calendarReminders: UserCalendarReminder[],
): { count: number; title: string | null } {
  const tomorrow = tomorrowDateStr();
  const dayStart = `${tomorrow}T00:00:00.000Z`;
  const dayEnd = `${tomorrow}T23:59:59.999Z`;

  const fromCalendar = calendarReminders.filter(
    (r) => r.entry_date === tomorrow && /interview/i.test(r.title ?? ''),
  );

  const fromApps = applications.filter((app) => {
    if (!['interview_scheduled', 'interview', 'interviewing', 'interview_completed'].includes(app.application_status)) {
      return false;
    }
    const when = app.follow_up_date;
    if (!when) return false;
    return when >= dayStart && when <= dayEnd;
  });

  const count = fromCalendar.length + fromApps.length;
  const title =
    fromCalendar[0]?.title
    ?? fromApps[0]?.job_title
    ?? null;

  return { count, title };
}

export function buildDailyBriefViewModel(
  jobs: DailyBriefJobInput[],
  applications: JobApplication[],
  calendarReminders: UserCalendarReminder[] = [],
): DailyBriefViewModel {
  const matched = jobs.filter((j) => j.score >= 35);
  const followUpsDue = countFollowUpsDue(applications);
  const interviews = countInterviewsTomorrow(applications, calendarReminders);

  const salaryJobs = matched.map((j) => ({
    salary_min: j.salaryMin,
    salary_max: j.salaryMax,
  }));

  let highestSalaryLabel = highestSalaryLabelFromJobs(salaryJobs);
  if (!highestSalaryLabel && salaryJobs.length > 0) {
    const withSalary = matched
      .map((j) => j.salaryMax ?? j.salaryMin)
      .filter((v): v is number => v != null && v > 0);
    if (withSalary.length > 0) {
      const max = Math.max(...withSalary);
      const lpa = max >= 100_000 ? max / 100_000 : max;
      highestSalaryLabel = formatLpaRange(lpa, lpa);
    }
  }

  const model: DailyBriefViewModel = {
    newMatchCount: matched.length,
    strongMatchCount: matched.filter((j) => j.score >= 90).length,
    highestSalaryLabel,
    followUpsDue,
    interviewsTomorrow: interviews.count,
    interviewTitle: interviews.title,
    hasContent:
      matched.length > 0
      || followUpsDue > 0
      || interviews.count > 0,
  };

  return model;
}
